import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  BarChart3, Download, Users, Clock, Calendar, TrendingUp, ChevronLeft, ChevronRight,
  LogOut, FileSpreadsheet, Building2, Target, Activity, DollarSign, Briefcase, Calculator,
  PieChart as PieChartIcon, LayoutDashboard, Settings, Bell, Search, Plus, Filter,
  FileText, ClipboardList, UserPlus, Skull
} from 'lucide-react';

import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, Area, AreaChart } from 'recharts';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API } from '@/config/api';

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const AdminDashboard = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Authentication Guard
  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  const [stats, setStats] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [showNewCommessa, setShowNewCommessa] = useState(false);
  const [newCommessa, setNewCommessa] = useState({ code: '', name: '', budget: '', other_costs: '', keywords: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [aiQuery, setAiQuery] = useState('');
  const [aiResults, setAiResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [weeklyDigest, setWeeklyDigest] = useState(null);
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'member', hourly_rate: '' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statsRes, monthRes, usersRes, commesseRes] = await Promise.all([
          axios.get(`${API}/stats/dashboard`),
          axios.get(`${API}/export/month/${selectedYear}/${selectedMonth}`),
          axios.get(`${API}/users`),
          axios.get(`${API}/commesse`)
        ]);
        setStats(statsRes.data);
        setMonthData(monthRes.data);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        setCommesse(Array.isArray(commesseRes.data) ? commesseRes.data : []);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const fetchWeekly = async () => {
      try {
        const res = await axios.get(`${API}/stats/weekly-digest`);
        setWeeklyDigest(res.data);
      } catch (err) {
        console.error('Error fetching weekly digest:', err);
      }
    };
    fetchWeekly();
  }, [selectedYear, selectedMonth]);

  const refreshUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error refreshing users:', err);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email) return;
    try {
      await axios.post(`${API}/users/create-member`, {
        ...newUser,
        hourly_rate: parseFloat(newUser.hourly_rate) || 0
      });
      setShowNewUser(false);
      setNewUser({ name: '', email: '', role: 'member', hourly_rate: '' });
      refreshUsers();
    } catch (err) {
      console.error('Error adding collaborator:', err);
      alert(err.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleUpdateUser = async (id, data) => {
    try {
      await axios.patch(`${API}/users/${id}`, data);
      await refreshUsers();
    } catch (err) {
      console.error('Error updating user:', err);
    }
  };

  const handleUpdateCommessa = async (id, data) => {
    try {
      await axios.patch(`${API}/commesse/${id}`, data);
      const res = await axios.get(`${API}/commesse`);
      setCommesse(res.data);
    } catch (err) {
      console.error('Error updating commessa:', err);
    }
  };

  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

  const changeMonth = (delta) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  // Prepare chart data
  const safeMonthData = monthData || {};
  const totalHours = safeMonthData.total_hours || 0;

  const pieData = safeMonthData.by_commessa ? Object.entries(safeMonthData.by_commessa).map(([name, data]) => ({
    name,
    value: data?.total || 0,
    percentage: totalHours > 0 ? (((data?.total || 0) / totalHours) * 100).toFixed(1) : "0"
  })).sort((a, b) => b.value - a.value) : [];

  const barData = safeMonthData.by_user ? Object.entries(safeMonthData.by_user).map(([name, data]) => ({
    name,
    ore: data?.total || 0
  })).sort((a, b) => b.ore - a.ore) : [];

  // Calculate average hours per day
  const avgHoursPerDay = monthData && monthData.entries?.length > 0
    ? (monthData.total_hours / new Set(monthData.entries.map(e => e.date)).size).toFixed(1)
    : 0;

  // Filter entries
  const filteredEntries = monthData?.entries?.filter(entry => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return entry.user_name?.toLowerCase().includes(query) ||
      entry.commessa_name?.toLowerCase().includes(query) ||
      entry.project_name?.toLowerCase().includes(query) ||
      entry.description?.toLowerCase().includes(query);
  }) || [];

  // Professional & Mafia-style XLSX Export 🤌
  const exportToExcel = () => {
    if (!monthData || !monthData.entries) return;

    const wb = XLSX.utils.book_new();
    const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;

    // 1. SUMMARY SHEET (Resoconto Generale) 👑
    const totalHours = monthData.total_hours || 0;
    const totalCost = monthData.total_cost || 0;
    const mvp = Object.entries(monthData.by_user || {}).sort((a, b) => b[1].total - a[1].total)[0];
    const topCommessa = Object.entries(monthData.by_commessa || {}).sort((a, b) => b[1].total - a[1].total)[0];

    const summaryData = [
      ['DON DOIT - REPORT MENSILE 🤌'],
      ['Periodo:', dateStr],
      [''],
      ['KPI GENERALI'],
      ['Ore Totali:', totalHours],
      ['Costo Totale Interno:', `€ ${totalCost.toLocaleString()}`],
      ['Numero Collaboratori:', Object.keys(monthData.by_user || {}).length],
      ['Numero Commesse Attive:', Object.keys(monthData.by_commessa || {}).length],
      [''],
      ['TOP PERFORMANCE'],
      ['MVP (Più Ore):', mvp ? `${mvp[0]} (${mvp[1].total}h)` : 'N/A'],
      ['Commessa Dominante:', topCommessa ? `${topCommessa[0]} (${topCommessa[1].total}h)` : 'N/A'],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet['!cols'] = [{ wch: 25 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, summarySheet, "RESOCONTO");

    // 2. ORE PER COMMESSA (User Breakdown) 👥
    const breakdownHeaders = ['COMMESSA', 'COLLABORATORE', 'ORE TOTALI'];
    const breakdownRows = [];
    Object.entries(monthData.by_commessa || {})
      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
      .forEach(([commessaName, data]) => {
        Object.entries(data.by_user || {})
          .sort(([userA], [userB]) => userA.localeCompare(userB))
          .forEach(([userName, hours]) => {
            breakdownRows.push([commessaName, userName, hours]);
          });
      });
    const breakdownSheet = XLSX.utils.aoa_to_sheet([breakdownHeaders, ...breakdownRows]);
    breakdownSheet['!cols'] = [{ wch: 25 }, { wch: 20 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, breakdownSheet, "ORE PER COMMESSA");

    // 3. ANALISI PROFITTO (The Vault) 💰
    const analysisHeaders = ['COMMESSA', 'ORE TOTALI', 'BUDGET (€)', 'COSTO INTERNO (€)', 'ALTRI COSTI (€)', 'MARGINE NETTO (€)', 'ROI (%)', 'STATUS'];
    const analysisRows = Object.entries(monthData.by_commessa || {})
      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
      .map(([name, data]) => {
        const totalCosti = (data.cost || 0) + (data.other_costs || 0);
        const margin = (data.budget || 0) - totalCosti;
        const roi = totalCosti > 0 ? (margin / totalCosti) * 100 : 0;
        const status = margin >= 0 ? '🟢 PROFIT' : '🔴 LOSS';
        return [
          name,
          data.hours || data.total,
          data.budget || 0,
          data.cost || 0,
          data.other_costs || 0,
          margin,
          roi.toFixed(1) + '%',
          status
        ];
      });
    const analysisSheet = XLSX.utils.aoa_to_sheet([analysisHeaders, ...analysisRows]);
    analysisSheet['!cols'] = [
      { wch: 25 }, { wch: 12 }, { wch: 15 }, { wch: 18 },
      { wch: 15 }, { wch: 18 }, { wch: 12 }, { wch: 15 }
    ];
    XLSX.utils.book_append_sheet(wb, analysisSheet, "ANALISI PROFITTO");

    // 4. DETTAGLIO ORE (The Ledger) 📜
    const entryHeaders = ['DATA', 'COLLABORATORE', 'CODICE', 'COMMESSA', 'PROGETTO', 'DESCRIZIONE', 'ORE'];
    const entryRows = [...monthData.entries]
      .sort((a, b) => a.commessa_name.localeCompare(b.commessa_name) || a.date.localeCompare(b.date))
      .map(e => [
        e.date,
        e.user_name,
        e.commessa_code,
        e.commessa_name,
        e.project_name || '-',
        e.description,
        e.hours
      ]);
    const entriesSheet = XLSX.utils.aoa_to_sheet([entryHeaders, ...entryRows]);
    entriesSheet['!cols'] = [
      { wch: 12 }, { wch: 18 }, { wch: 10 }, { wch: 20 },
      { wch: 20 }, { wch: 40 }, { wch: 8 }
    ];
    XLSX.utils.book_append_sheet(wb, entriesSheet, "DETTAGLIO ORE");

    // 5. PERFORMANCE SOLDATI (The Family) 🔫
    const userHeaders = ['NOME', 'ORE TOTALI', 'COSTO AZIENDALE', 'MEDIA ORE/GG'];
    const userRows = Object.entries(monthData.by_user || {})
      .sort(([, a], [, b]) => b.total - a.total)
      .map(([name, data]) => [
        name,
        data.total,
        `€ ${data.cost.toLocaleString()}`,
        (data.total / 20).toFixed(1) // Assuming 20 working days
      ]);
    const userSheet = XLSX.utils.aoa_to_sheet([userHeaders, ...userRows]);
    userSheet['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 18 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, userSheet, "PERFORMANCE COLLABORATORI");

    // Save File
    XLSX.writeFile(wb, `Business_Report_${dateStr}_🤌.xlsx`);
  };

  // Create new commessa
  const handleCreateCommessa = async () => {
    if (!newCommessa.code || !newCommessa.name) return;
    try {
      await axios.post(`${API}/commesse`, {
        code: newCommessa.code.toUpperCase(),
        name: newCommessa.name,
        budget: parseFloat(newCommessa.budget) || 0,
        other_costs: parseFloat(newCommessa.other_costs) || 0,
        keywords: newCommessa.keywords.split(',').map(k => k.trim().toLowerCase()).filter(k => k)
      });
      // Refresh commesse
      const res = await axios.get(`${API}/commesse`);
      setCommesse(res.data);
      setShowNewCommessa(false);
      setNewCommessa({ code: '', name: '', budget: '', other_costs: '', keywords: '' });
    } catch (err) {
      console.error('Error creating commessa:', err);
    }
  };

  // AI Search (The Snitch)
  const handleAiSearch = async () => {
    if (!aiQuery) return;
    setIsSearching(true);
    try {
      const res = await axios.post(`${API}/ai/search`, { query: aiQuery });
      setAiResults(res.data.results);
    } catch (err) {
      console.error('AI search failed:', err);
    } finally {
      setIsSearching(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-500">Caricamento dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 light">
      {/* Professional Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20">
                  <LayoutDashboard className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900">QuiteBold Analysis Engine</h1>
                  <p className="text-xs text-slate-500">QuiteBold Studio</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" className="text-slate-600">
                <Bell className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" className="text-slate-600">
                <Settings className="w-4 h-4" />
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500">Amministratore</p>
                </div>
                <Button variant="ghost" size="sm" onClick={logout} className="text-slate-600 hover:text-red-600">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Period selector and actions */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Button variant="outline" size="sm" onClick={() => changeMonth(-1)} className="hover:bg-slate-100">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h2 className="text-2xl font-bold text-slate-900 min-w-[200px] text-center">
                {monthNames[selectedMonth - 1]} {selectedYear}
              </h2>
              <Button variant="outline" size="sm" onClick={() => changeMonth(1)} className="hover:bg-slate-100">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-slate-500 ml-1">Report mensile delle ore lavorate</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Dialog open={showNewCommessa} onOpenChange={setShowNewCommessa}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuova Commessa
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200">
                <DialogHeader>
                  <DialogTitle className="text-slate-900">Aggiungi Nuova Commessa</DialogTitle>
                  <DialogDescription className="text-slate-500 text-xs">
                    Inserisci i dettagli del nuovo cliente o progetto per il sistema.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <label className="text-sm text-slate-600 mb-2 block font-medium">Codice Commessa</label>
                    <Input
                      value={newCommessa.code}
                      onChange={(e) => setNewCommessa(prev => ({ ...prev, code: e.target.value }))}
                      placeholder="es. SOC-0007"
                      className="border-slate-200"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-2 block font-medium">Nome Cliente</label>
                    <Input
                      value={newCommessa.name}
                      onChange={(e) => setNewCommessa(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Nome del cliente"
                      className="border-slate-200"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-2 block font-medium">Budget (€)</label>
                    <Input
                      type="number"
                      value={newCommessa.budget}
                      onChange={(e) => setNewCommessa(prev => ({ ...prev, budget: e.target.value }))}
                      placeholder="es. 5000"
                      className="border-slate-200"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-2 block font-medium">Altri Costi (€)</label>
                    <Input
                      type="number"
                      value={newCommessa.other_costs}
                      onChange={(e) => setNewCommessa(prev => ({ ...prev, other_costs: e.target.value }))}
                      placeholder="es. 200"
                      className="border-slate-200"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-2 block font-medium">Keywords (separate da virgola)</label>
                    <Input
                      value={newCommessa.keywords}
                      onChange={(e) => setNewCommessa(prev => ({ ...prev, keywords: e.target.value }))}
                      placeholder="keyword1, keyword2..."
                      className="border-slate-200"
                    />
                  </div>
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={handleCreateCommessa}
                    disabled={!newCommessa.code || !newCommessa.name}
                  >
                    Aggiungi Commessa
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button onClick={exportToExcel} className="bg-slate-900 hover:bg-slate-800 text-white shadow-lg shadow-slate-900/20">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Esporta Report XLSX
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium mb-1">Ore Totali Mese</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.month_hours?.toFixed(1) || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">ore lavorate</p>
                </div>
                <div className="p-3 rounded-xl bg-blue-50">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium mb-1">Costo Interno</p>
                  <p className="text-3xl font-bold text-slate-900">€ {stats?.total_cost?.toLocaleString() || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">salari + collaboratori</p>
                </div>
                <div className="p-3 rounded-xl bg-red-50">
                  <Calculator className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium mb-1">Budget Attivo</p>
                  <p className="text-3xl font-bold text-slate-900">€ {stats?.total_budget?.toLocaleString() || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">progetti in corso</p>
                </div>
                <div className="p-3 rounded-xl bg-green-50">
                  <Briefcase className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium mb-1">Margine Finale</p>
                  <p className={`text-3xl font-bold ${(stats?.total_margin || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    € {stats?.total_margin?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">netto del mese</p>
                </div>
                <div className="p-3 rounded-xl bg-blue-50">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium mb-1">Team Members</p>
                  <p className="text-3xl font-bold text-slate-900">{Object.keys(monthData?.by_user || {}).length}</p>
                  <p className="text-xs text-slate-400 mt-1">attivi questo mese</p>
                </div>
                <div className="p-3 rounded-xl bg-orange-50">
                  <Users className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 shadow-sm">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Panoramica
            </TabsTrigger>
            <TabsTrigger value="commesse" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <Briefcase className="w-4 h-4 mr-2" />
              Commesse
            </TabsTrigger>
            <TabsTrigger value="team" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <Users className="w-4 h-4 mr-2" />
              Team
            </TabsTrigger>
            <TabsTrigger value="detail" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <BarChart3 className="w-4 h-4 mr-2" />
              Dettaglio
            </TabsTrigger>
            <TabsTrigger value="clients" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <Building2 className="w-4 h-4 mr-2" />
              Anagrafica
            </TabsTrigger>
            <TabsTrigger value="profitability" className="data-[state=active]:bg-green-50 data-[state=active]:text-green-700">
              <DollarSign className="w-4 h-4 mr-2" />
              ROI & Profitto
            </TabsTrigger>
            <TabsTrigger value="weekly" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <FileText className="w-4 h-4 mr-2" />
              Analisi Settimanale
            </TabsTrigger>
            <TabsTrigger value="snitch" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <Search className="w-4 h-4 mr-2" />
              Ricerca Intelligente
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
                    <PieChartIcon className="w-5 h-5 text-blue-600" />
                    Distribuzione per Commessa
                  </CardTitle>
                  <CardDescription>Ripartizione ore lavorate</CardDescription>
                </CardHeader>
                <CardContent>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percentage }) => `${percentage}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => [`${value}h`, 'Ore']}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-slate-400">
                      Nessun dato disponibile
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Bar Chart */}
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-green-600" />
                    Ore per Team Member
                  </CardTitle>
                  <CardDescription>Performance individuale</CardDescription>
                </CardHeader>
                <CardContent>
                  {barData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={barData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" stroke="#64748b" fontSize={12} />
                        <YAxis dataKey="name" type="category" stroke="#64748b" fontSize={12} width={80} />
                        <Tooltip
                          formatter={(value) => [`${value}h`, 'Ore']}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Bar dataKey="ore" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-slate-400">
                      Nessun dato disponibile
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Commesse Tab */}
          <TabsContent value="commesse">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg text-slate-900">Analisi Dettagliata Commesse</CardTitle>
                <CardDescription>Performance e allocazione risorse per cliente</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {commesse
                    .sort((a, b) => (monthData?.by_commessa?.[b.name]?.total || 0) - (monthData?.by_commessa?.[a.name]?.total || 0))
                    .map((commessa, index) => {
                      const data = monthData?.by_commessa?.[commessa.name] || { total: 0, cost: 0 };
                      const totalCosti = (data.cost || 0) + (commessa.other_costs || 0);
                      const margine = (commessa.budget || 0) - totalCosti;
                      const percentage = monthData?.total_hours > 0 ? ((data.total / monthData.total_hours) * 100) : 0;

                      return (
                        <div key={commessa.id} className={`p-4 rounded-xl transition-colors border ${data.total > 0 ? 'bg-slate-50 border-blue-100' : 'bg-white border-slate-100 opacity-60'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: data.total > 0 ? CHART_COLORS[index % CHART_COLORS.length] : '#cbd5e1' }}
                              />
                              <div>
                                <span className="font-semibold text-slate-900 block">{commessa.name}</span>
                                <span className="text-[10px] text-slate-400 font-mono">{commessa.code}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-6">
                              <div className="text-right">
                                <span className="text-xs text-slate-500 block">Ore Mese</span>
                                <span className="font-bold text-slate-900">{data.total.toFixed(1)}h</span>
                              </div>
                              <div className="text-right">
                                <span className={`text-xs block ${margine >= 0 ? 'text-green-600' : 'text-red-500'}`}>Margine</span>
                                <span className={`font-bold ${margine >= 0 ? 'text-green-600' : 'text-red-500'}`}>€ {margine.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>

                          <Progress value={percentage} className="h-1.5 mb-4" />

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2 border-t border-slate-100">
                            <div>
                              <label className="text-[9px] font-bold text-slate-400 uppercase block mb-1">Budget (€)</label>
                              <Input
                                type="number"
                                defaultValue={commessa.budget || 0}
                                className="h-7 text-xs border-slate-200"
                                onBlur={(e) => handleUpdateCommessa(commessa.id, { budget: parseFloat(e.target.value) || 0 })}
                              />
                            </div>
                            <div>
                              <label className="text-[9px] font-bold text-slate-400 uppercase block mb-1">Altri Costi (€)</label>
                              <Input
                                type="number"
                                defaultValue={commessa.other_costs || 0}
                                className="h-7 text-xs border-slate-200"
                                onBlur={(e) => handleUpdateCommessa(commessa.id, { other_costs: parseFloat(e.target.value) || 0 })}
                              />
                            </div>
                            <div className="flex flex-col justify-center">
                              <span className="text-[9px] font-bold text-slate-400 uppercase block">Costo Interno:</span>
                              <span className="text-xs text-slate-600">€ {(data.cost || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex flex-col justify-center text-right">
                              <span className="text-[9px] font-bold text-slate-400 uppercase block">Costo Totale:</span>
                              <span className="text-xs font-bold text-slate-900">€ {totalCosti.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Tab - Professional Version */}
          <TabsContent value="team">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-slate-900">Performance Collaboratori</CardTitle>
                  <CardDescription>Analisi produttività e allocazione oraria</CardDescription>
                </div>
                <Dialog open={showNewUser} onOpenChange={setShowNewUser}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="border-blue-200 text-blue-600 hover:bg-blue-50">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Aggiungi Collaboratore
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border-slate-200">
                    <DialogHeader>
                      <DialogTitle className="text-slate-900">Nuovo Collaboratore</DialogTitle>
                      <DialogDescription className="text-slate-500 text-xs">
                        Inserisci i dettagli per registrare un nuovo profilo aziendale.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Nome Completo</label>
                        <Input
                          placeholder="Mario Rossi"
                          value={newUser.name}
                          onChange={(e) => setNewUser(p => ({ ...p, name: e.target.value }))}
                          className="border-slate-200"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Email Aziendale</label>
                        <Input
                          type="email"
                          placeholder="m.rossi@quitebold.studio"
                          value={newUser.email}
                          onChange={(e) => setNewUser(p => ({ ...p, email: e.target.value }))}
                          className="border-slate-200"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Ruolo</label>
                        <Select onValueChange={(val) => setNewUser(p => ({ ...p, role: val }))} defaultValue={newUser.role}>
                          <SelectTrigger className="border-slate-200">
                            <SelectValue placeholder="Seleziona ruolo..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="member">Membro (Operativo)</SelectItem>
                            <SelectItem value="manager">Manager (Gestione)</SelectItem>
                            <SelectItem value="admin">Amministratore (Pieno Accesso)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Costo Orario (€)</label>
                        <Input
                          type="number"
                          placeholder="es. 40"
                          value={newUser.hourly_rate}
                          onChange={(e) => setNewUser(p => ({ ...p, hourly_rate: e.target.value }))}
                          className="border-slate-200"
                        />
                      </div>
                      <Button onClick={handleAddUser} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-6">
                        Registra Collaboratore
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {users.sort((a, b) => {
                    const hA = monthData?.by_user?.[a.name]?.total || 0;
                    const hB = monthData?.by_user?.[b.name]?.total || 0;
                    return hB - hA;
                  }).map((member, idx) => {
                    const hours = monthData?.by_user?.[member.name]?.total || 0;
                    const hoursToday = stats?.today_by_user?.[member.name] || 0;

                    return (
                      <div key={member.id} className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600">
                          {member.name.charAt(0)}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <h4 className="font-bold text-slate-900">{member.name}</h4>
                            <div className="flex items-center gap-2">
                              {hoursToday === 0 && member.role !== 'admin' && (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API}/push/minaccia/${member.id}`);
                                      alert(`Minaccia inviata a ${member.name}! 🤌`);
                                    } catch (err) {
                                      console.error("Failed to send minaccia:", err);
                                    }
                                  }}
                                  className="h-7 text-[10px] bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
                                >
                                  <Skull className="w-3 h-3 mr-1" />
                                  RICHIAMA ALL'ORDINE
                                </Button>
                              )}
                              <Badge variant="outline" className="capitalize text-[10px]">
                                {member.role}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex flex-col">
                              <span className="text-slate-500 text-xs">{member.email}</span>
                              <span className="text-xs font-medium text-slate-400">
                                Oggi: <span className={hoursToday > 0 ? "text-green-600" : "text-red-500"}>{hoursToday.toFixed(1)}h</span>
                              </span>
                            </div>
                            <span className="font-bold text-slate-900">{hours.toFixed(1)}h nel mese</span>
                          </div>

                          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold text-slate-400 uppercase">Costo Orario:</span>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-400">€</span>
                                <Input
                                  type="number"
                                  className="w-16 h-7 text-xs border-slate-200"
                                  defaultValue={member.hourly_rate || 0}
                                  onBlur={(e) => handleUpdateUser(member.id, { hourly_rate: parseFloat(e.target.value) || 0 })}
                                />
                              </div>
                            </div>
                            <div className="h-1.5 w-32 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-slate-900"
                                style={{ width: `${Math.min((hours / 160) * 100, 100)}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Detail Tab */}
          <TabsContent value="detail">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg text-slate-900">Registro Completo</CardTitle>
                    <CardDescription>{filteredEntries.length} registrazioni trovate</CardDescription>
                  </div>
                  <div className="relative w-full md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Cerca..."
                      className="pl-10 border-slate-200"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-semibold">Data</TableHead>
                        <TableHead className="font-semibold">Utente</TableHead>
                        <TableHead className="font-semibold">Codice</TableHead>
                        <TableHead className="font-semibold">Commessa</TableHead>
                        <TableHead className="font-semibold">Progetto</TableHead>
                        <TableHead className="font-semibold">Descrizione</TableHead>
                        <TableHead className="font-semibold text-right">Ore</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEntries.slice(0, 50).map((entry) => (
                        <TableRow key={entry.id} className="hover:bg-slate-50">
                          <TableCell className="font-medium">{entry.date}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{entry.user_name}</Badge>
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-slate-100 px-2 py-1 rounded">{entry.commessa_code}</code>
                          </TableCell>
                          <TableCell>{entry.commessa_name}</TableCell>
                          <TableCell>{entry.project_name || <span className="text-slate-300">—</span>}</TableCell>
                          <TableCell className="max-w-xs truncate text-slate-600">{entry.description}</TableCell>
                          <TableCell className="text-right font-bold text-blue-600">{entry.hours}h</TableCell>
                        </TableRow>
                      ))}
                      {filteredEntries.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-slate-400 py-12">
                            Nessuna registrazione trovata
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  {filteredEntries.length > 50 && (
                    <p className="text-center text-sm text-slate-500 mt-4">
                      Mostrate 50 di {filteredEntries.length} registrazioni. Esporta per vedere tutti i dati.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profitability Tab */}
          <TabsContent value="profitability" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-white border-green-200 shadow-sm border-l-4 border-l-green-500">
                <CardContent className="p-6">
                  <p className="text-sm text-slate-500 font-medium mb-1">Budget Totale Mese</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {Object.values(monthData?.by_commessa || {}).reduce((sum, c) => sum + (c.budget || 0), 0).toLocaleString()}€
                  </p>
                  <p className="text-xs text-slate-400 mt-1">valore commesse attive</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-red-200 shadow-sm border-l-4 border-l-red-500">
                <CardContent className="p-6">
                  <p className="text-sm text-slate-500 font-medium mb-1">Costo Interno (Stima)</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {monthData?.total_cost?.toLocaleString()}€
                  </p>
                  <p className="text-xs text-slate-400 mt-1">basato su ore e tariffe</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-600 to-green-700 text-white shadow-lg border-none">
                <CardContent className="p-6">
                  <p className="text-sm text-green-100 font-medium mb-1">Margine Operativo</p>
                  <p className="text-3xl font-bold">
                    {(Object.values(monthData?.by_commessa || {}).reduce((sum, c) => sum + (c.budget || 0), 0) - (monthData?.total_cost || 0)).toLocaleString()}€
                  </p>
                  <p className="text-xs text-green-200 mt-1">ROI stimato: {(((Object.values(monthData?.by_commessa || {}).reduce((sum, c) => sum + (c.budget || 0), 0) - (monthData?.total_cost || 0)) / (monthData?.total_cost || 1)) * 100).toFixed(0)}%</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg text-slate-900">Analisi Margine per Commessa</CardTitle>
                <CardDescription>Quali lavori sono davvero "worth it"?</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Commessa</TableHead>
                      <TableHead className="text-right">Budget</TableHead>
                      <TableHead className="text-right">Costo Ore</TableHead>
                      <TableHead className="text-right">Margine</TableHead>
                      <TableHead className="text-right">% ROI</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(monthData?.by_commessa || {})
                      .sort(([, a], [, b]) => (b.budget - b.cost) - (a.budget - a.cost))
                      .map(([name, data]) => {
                        const margin = (data.budget || 0) - (data.cost || 0);
                        const roi = data.cost > 0 ? (margin / data.cost) * 100 : 0;
                        return (
                          <TableRow key={name} className="hover:bg-slate-50">
                            <TableCell className="font-semibold">{name}</TableCell>
                            <TableCell className="text-right">{data.budget?.toLocaleString()}€</TableCell>
                            <TableCell className="text-right text-red-500">-{data.cost?.toLocaleString()}€</TableCell>
                            <TableCell className={`text-right font-bold ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {margin.toLocaleString()}€
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge className={roi > 50 ? 'bg-green-100 text-green-700' : roi > 0 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}>
                                {roi.toFixed(0)}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg text-slate-900">Anagrafica Commesse</CardTitle>
                    <CardDescription>{commesse.length} commesse registrate</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-semibold">Codice</TableHead>
                        <TableHead className="font-semibold">Nome</TableHead>
                        <TableHead className="font-semibold">Keywords</TableHead>
                        <TableHead className="font-semibold">Tipo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {commesse.map((c) => (
                        <TableRow key={c.id} className="hover:bg-slate-50">
                          <TableCell>
                            <code className="text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded font-semibold">
                              {c.code}
                            </code>
                          </TableCell>
                          <TableCell className="font-medium">{c.name}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {c.keywords?.map((k, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {k}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            {c.is_default ? (
                              <Badge className="bg-green-100 text-green-700">Default</Badge>
                            ) : (
                              <Badge variant="secondary">Custom</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Weekly Analysis Tab */}
          <TabsContent value="weekly" className="space-y-6">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-900">
                  <ClipboardList className="w-5 h-5 text-blue-600" />
                  Analisi Operativa Settimanale
                </CardTitle>
                <CardDescription>Sintesi automatica dell'attività degli ultimi 7 giorni</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-slate-50 border border-slate-100 p-6 rounded-2xl">
                  <p className="text-lg font-medium text-slate-800 leading-relaxed">
                    {weeklyDigest?.summary}
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Ore Totali', value: weeklyDigest?.kpi?.total_hours, icon: Clock },
                    { label: 'Membro più attivo', value: weeklyDigest?.kpi?.mvp, icon: Activity },
                    { label: 'Utenti Attivi', value: weeklyDigest?.kpi?.active_users, icon: Users },
                    { label: 'Media/Utente', value: `${weeklyDigest?.kpi?.avg_hours_per_user}h`, icon: TrendingUp }
                  ].map((kpi, i) => (
                    <div key={i} className="bg-white p-4 rounded-xl border border-slate-100 text-center">
                      <kpi.icon className={`w-5 h-5 mx-auto mb-2 text-slate-400`} />
                      <p className="text-xs text-slate-500 font-bold uppercase mb-1">{kpi.label}</p>
                      <p className="text-xl font-bold text-slate-900">{kpi.value || 0}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Smart Search Tab */}
          <TabsContent value="snitch" className="space-y-6">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-blue-600" />
                  Ricerca Intelligente
                </CardTitle>
                <CardDescription>Individuazione registrazioni nei database degli ultimi 90 giorni tramite query testuale.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex gap-2">
                  <Input
                    value={aiQuery}
                    onChange={(e) => setAiQuery(e.target.value)}
                    placeholder="Esempio: 'Interventi su Procida' o 'Dettagli commessa Podcastory'"
                    className="border-slate-200"
                    onKeyDown={(e) => e.key === 'Enter' && handleAiSearch()}
                  />
                  <Button onClick={handleAiSearch} disabled={isSearching} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {isSearching ? <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : "Esegui Ricerca"}
                  </Button>
                </div>

                <div className="space-y-3">
                  {aiResults.map((res, i) => (
                    <div key={i} className="p-4 rounded-lg bg-slate-50 border border-slate-200 hover:border-blue-200 transition-colors">
                      <div className="flex justify-between text-xs text-slate-500 mb-2">
                        <span>{res.date}</span>
                        <span className="font-bold text-slate-700 uppercase tracking-tighter">{res.user_name}</span>
                      </div>
                      <p className="text-sm font-semibold text-slate-900 mb-1">{res.commessa_name} {res.project_name && `(${res.project_name})`}</p>
                      <p className="text-xs text-slate-600">"{res.description}"</p>
                    </div>
                  ))}
                  {!isSearching && aiResults.length === 0 && aiQuery && (
                    <div className="py-12 text-center text-slate-400">
                      Nessun risultato corrispondente ai criteri inseriti.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Professional Footer */}
      <footer className="border-t border-slate-200 bg-white mt-12">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                <LayoutDashboard className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">QuiteBold Analysis Engine</p>
                <p className="text-xs text-slate-500">© 2025 - QuiteBold Studio</p>
              </div>
            </div>
            <div className="text-xs text-slate-400">
              Versione 1.0.0 • Sviluppato con React & FastAPI
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdminDashboard;
